import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { CopyButton, Section } from "./AiHtml";
import { Checklist, Opportunities, PagesCrawled, PerformanceStrip, PriorityActions } from "./AuditDetails";

const Ring = ({ label, value, testId }) => {
  const c = value >= 75 ? "#10B981" : value >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="card-n p-5 flex items-center gap-4" data-testid={testId}>
      <div className="relative w-16 h-16 rounded-full grid place-items-center" style={{ background: `conic-gradient(${c} ${value * 3.6}deg, #1E293B 0)` }}>
        <div className="absolute inset-1.5 rounded-full bg-[#0F172A] grid place-items-center font-display font-bold text-lg">{value}</div>
      </div>
      <div>
        <p className="label-mono">{label}</p>
        <p className="text-xs text-slate-500 mt-1">out of 100</p>
      </div>
    </div>
  );
};

const StatusIcon = ({ s }) =>
  s === "good" ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" /> : s === "warning" ? <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" /> : <XCircle size={16} className="text-red-400 shrink-0 mt-0.5" />;

const StatusList = ({ items, k }) => (
  <ul className="space-y-3">
    {(items || []).map((x, i) => (
      <li key={i} className="flex gap-3 text-sm">
        <StatusIcon s={x.status} />
        <div><p className="text-slate-100 font-medium">{x[k]}</p><p className="text-slate-400">{x.detail}</p></div>
      </li>
    ))}
  </ul>
);

export const AuditReport = ({ r }) => (
  <div className="space-y-6" data-testid="audit-report">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Ring label="SEO score" value={r.seo_score} testId="audit-seo-score" />
      <Ring label="AEO readiness" value={r.aeo_score} testId="audit-aeo-score" />
      <Ring label="GEO readiness" value={r.geo_score} testId="audit-geo-score" />
      <Ring label="Social consistency" value={r.social_score} testId="audit-social-score" />
    </div>
    <PerformanceStrip p={r.performance} />
    <Section title="Executive summary">
      <p className="text-sm text-slate-300 leading-relaxed">{r.summary}</p>
      {r.crawl?.error && <p className="text-xs text-amber-400 mt-3">{r.crawl.error}</p>}
      {r.strengths?.length > 0 && <div className="flex flex-wrap gap-2 mt-4">{r.strengths.map((s, i) => <span key={i} className="badge-o">{s}</span>)}</div>}
    </Section>
    <PriorityActions items={r.priority_actions} />
    <Checklist checks={r.checks} />
    <Opportunities r={r} />
    <div className="grid lg:grid-cols-2 gap-6">
      <Section title="Content gaps" testId="audit-content-gaps">
        <div className="space-y-3">
          {(r.content_gaps || []).map((g, i) => (
            <div key={i} className="border-l-2 border-orange-500 pl-3"><p className="text-sm font-medium text-slate-100">{g.title}</p><p className="text-sm text-slate-400">{g.detail}</p></div>
          ))}
        </div>
      </Section>
      <Section title="AEO + GEO readiness"><StatusList items={r.aeo_geo} k="item" /></Section>
    </div>
    <Section title="Social consistency"><StatusList items={r.social_consistency} k="platform" /></Section>
    <PagesCrawled pages={r.pages} />
    <Section title="30-60-90 day plan" testId="audit-plan">
      <div className="grid md:grid-cols-3 gap-4">
        {[["d30", "First 30 days"], ["d60", "Days 31-60"], ["d90", "Days 61-90"]].map(([k, l]) => (
          <div key={k} className="rounded-lg bg-slate-900 border border-slate-800 p-4">
            <p className="label-mono mb-3">{l}</p>
            <ul className="space-y-2 text-sm text-slate-300">{(r.plan?.[k] || []).map((x, i) => <li key={i} className="flex gap-2"><span className="text-orange-500">→</span>{x}</li>)}</ul>
          </div>
        ))}
      </div>
    </Section>
    {r.schema_jsonld && (
      <Section title="Schema JSON-LD" right={<CopyButton testId="copy-schema-btn" text={r.schema_jsonld} label="Copy schema" />} testId="audit-schema">
        <pre className="text-xs text-slate-300 bg-[#070B14] p-4 rounded-lg overflow-auto max-h-72 scroll-thin">{(() => { try { return JSON.stringify(JSON.parse(r.schema_jsonld), null, 2); } catch { return r.schema_jsonld; } })()}</pre>
      </Section>
    )}
  </div>
);
